import "./accountBookings.css";
import { useState, useEffect, useContext } from "react";
import usersRequest from "../../../request/usersRequest";
import scheduleBasedRequest from "../../../request/scheduleBasedRequest";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import { useNavigate } from "react-router-dom";
import generalRequest from "../../../request/generalRequest";
import BookingModal from "../../bookingModal/bookingModal";
import { Calendar, Clock, RefreshCw, X, ChevronRight } from "lucide-react";
import AccountBookingsSkeleton from "./accountBookingsSkeleton/accountBookingsSkeleton";

const statusConfig = {
	completed: { label: "Completed", color: "#16a34a", bg: "#f0fdf4" },
	cancelled: { label: "Cancelled", color: "#dc2626", bg: "#fef2f2" },
	no_show: { label: "No show", color: "#9ca3af", bg: "#f9fafb" },
	rescheduled: { label: "Rescheduled", color: "#d97706", bg: "#fffbeb" },
	confirmed: { label: "Confirmed", color: "#1a5f8a", bg: "#e8f4fb" },
};

const StatusBadge = ({ status }) => {
	const cfg = statusConfig[status] || { label: status, color: "#5e7a91", bg: "#f4f6f9" };
	return (
		<span className="sf-ab-badge" style={{ color: cfg.color, background: cfg.bg }}>
			{cfg.label}
		</span>
	);
};

const AccountBookings = () => {
	const [bookings, setBookings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [isBookingOpen, setIsBookingOpen] = useState(false);
	const [rescheduleData, setRescheduleData] = useState(null);
	const [shopData, setShopData] = useState(null);
	const [confirmCancelId, setConfirmCancelId] = useState(null);

	const { userData, language } = useContext(CombinedContext);
	const navigate = useNavigate();

	const isLoggedIn = !!(userData && userData.cognitoTokens?.IdToken);
	const userId = userData?.user?.userId;

	useEffect(() => {
		async function loadBookings() {
			if (!isLoggedIn || !userId) {
				setLoading(false);
				return;
			}
			try {
				const data = await usersRequest.getUserBookings(userId, userData.cognitoTokens?.AccessToken);
				setBookings(data.bookings || []);
			} catch (err) {
				setError(err.message || "Could not load bookings");
			} finally {
				setLoading(false);
			}
		}
		loadBookings();
	}, [isLoggedIn, userId, userData?.cognitoTokens?.IdToken]);

	const now = new Date();
	const upcoming = bookings.filter((b) => new Date(b.startTime) > now && !["cancelled", "no_show"].includes(b.status));
	const history = bookings.filter((b) => new Date(b.startTime) <= now || ["cancelled", "no_show"].includes(b.status));

	const handleCardClick = (bookingId) => navigate(`/booking/${bookingId}`);

	async function handleCancel(bookingId) {
		try {
			await scheduleBasedRequest.cancelBooking(bookingId, userData.cognitoTokens?.IdToken);
			setBookings((prev) => prev.filter((b) => b.bookingId !== bookingId));
		} catch (err) {
			setError(err.message || "Could not cancel booking");
		}
	}

	const reloadBookings = async () => {
		try {
			const data = await usersRequest.getUserBookings(userId, userData.cognitoTokens?.AccessToken);
			setBookings(data.bookings || []);
		} catch (err) {
			setError(err.message || "Could not reload bookings");
		}
	};

	async function handleReschedule(booking) {
		try {
			const res = await generalRequest.getShopWithstaffsByIdPublic(booking.shopId);
			setShopData(res.shop);
			setRescheduleData(booking);
			setIsBookingOpen(true);
		} catch (err) {
			setError(err.message || "Unable to reschedule booking");
		}
	}

	const fmt = (iso, opts) => new Date(iso).toLocaleDateString(language, opts);
	const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

	if (loading) {
		return <AccountBookingsSkeleton />;
	}

	return (
		<div className="sf-account-bookings">
			{error && <div className="sf-ab-error">{error}</div>}

			{/* ── UPCOMING ── */}
			<section className="sf-ab-section">
				<h2 className="sf-ab-section-title">
					{translations[language].myBookings.upcomingTitle}
					{upcoming.length > 0 && <span className="sf-ab-count">{upcoming.length}</span>}
				</h2>

				{!loading && upcoming.length === 0 ? (
					<div className="sf-ab-empty">
						<Calendar size={28} strokeWidth={1.4} className="sf-ab-empty-icon" />
						<p>{translations[language].myBookings.noUpcoming}</p>
					</div>
				) : (
					<div className="sf-ab-upcoming-list">
						{upcoming.map((b) => (
							<div key={b.bookingId} className="sf-ab-card" onClick={() => handleCardClick(b.bookingId)}>
								<div className="sf-ab-card-accent" />

								<div className="sf-ab-card-body">
									<div className="sf-ab-card-left">
										<div className="sf-ab-shop-name">{b.shopName}</div>
										{b.services?.length > 0 && (
											<div className="sf-ab-service-names">{b.services.map((s) => s.serviceNameEn).join(" · ")}</div>
										)}
										<div className="sf-ab-datetime">
											<span className="sf-ab-chip">
												<Calendar size={11} />
												{fmt(b.startTime, { weekday: "short", month: "short", day: "numeric" })}
											</span>
											<span className="sf-ab-chip">
												<Clock size={11} />
												{fmtTime(b.startTime)}
											</span>
										</div>
									</div>

									<div className="sf-ab-card-right" onClick={(e) => e.stopPropagation()}>
										<button className="sf-ab-btn-reschedule" onClick={() => handleReschedule(b)}>
											<RefreshCw size={12} />
											{translations[language].myBookings.reschedule}
										</button>
										<button className="sf-ab-btn-cancel" onClick={() => setConfirmCancelId(b.bookingId)}>
											<X size={12} />
											{translations[language].myBookings.cancel}
										</button>
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</section>

			{/* ── HISTORY ── */}
			<section className="sf-ab-section">
				<h2 className="sf-ab-section-title">{translations[language].myBookings.historyTitle}</h2>

				{history.length === 0 ? (
					<div className="sf-ab-empty">
						<p>{translations[language].myBookings.noHistory}</p>
					</div>
				) : (
					<>
						{/* DESKTOP TABLE */}
						<div className="sf-ab-table-wrap">
							<table className="sf-history-table">
								<thead>
									<tr>
										<th>{translations[language].myBookings.table.date}</th>
										<th>{translations[language].myBookings.table.service}</th>
										<th>{translations[language].myBookings.table.shop}</th>
										<th>{translations[language].myBookings.table.status}</th>
									</tr>
								</thead>
								<tbody>
									{history.map((h) => (
										<tr key={h.bookingId} className="clickable" onClick={() => handleCardClick(h.bookingId)}>
											<td>{fmt(h.startTime, { month: "short", day: "numeric" })}</td>
											<td>
												{h.services?.map((s, i) => (
													<span key={s.serviceId}>
														{s.serviceNameEn}
														{i < h.services.length - 1 ? ", " : ""}
													</span>
												))}
											</td>
											<td>{h.shopName || "—"}</td>
											<td>
												<StatusBadge status={h.status} />
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{/* MOBILE CARDS */}
						<div className="sf-history-cards">
							{history.map((h) => (
								<div key={h.bookingId} className="sf-history-card clickable" onClick={() => handleCardClick(h.bookingId)}>
									<div className="sf-hc-top">
										<div className="sf-hc-date">{fmt(h.startTime, { month: "short", day: "numeric" })}</div>
										<StatusBadge status={h.status} />
									</div>
									{h.services?.length > 0 && (
										<div className="sf-hc-services">
											{h.services.map((s) => (
												<span key={s.serviceId} className="sf-bookings-service-pill">
													{s.serviceNameEn}
												</span>
											))}
										</div>
									)}
									<div className="sf-hc-shop">{h.shopName || "—"}</div>
								</div>
							))}
						</div>
					</>
				)}
			</section>

			{/* ── RESCHEDULE MODAL ── */}
			{isBookingOpen && shopData && rescheduleData && (
				<BookingModal
					open={isBookingOpen}
					onClose={() => {
						setIsBookingOpen(false);
						setRescheduleData(null);
						setShopData(null);
						reloadBookings();
					}}
					availabilityId={shopData.shopId}
					shopData={shopData}
					initialServices={rescheduleData.services}
					initialstaffId={rescheduleData.staffId}
					mode="reschedule"
					existingBooking={rescheduleData}
				/>
			)}
			{confirmCancelId && (
				<div className="sf-confirm-overlay">
					<div className="sf-confirm-modal">
						<h3>{translations[language].account.bookings.confirmCancelTitle}</h3>
						<p className="sf-confirm-text">{translations[language].account.bookings.confirmCancelText}</p>
						<div className="sf-confirm-actions">
							<button className="sf-confirm-btn ghost" onClick={() => setConfirmCancelId(null)}>
								{translations[language].account.bookings.confirmCancelNo}
							</button>
							<button
								className="sf-confirm-btn danger"
								onClick={async () => {
									await handleCancel(confirmCancelId);
									setConfirmCancelId(null);
								}}
							>
								{translations[language].account.bookings.confirmCancelYes}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AccountBookings;
